import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getTopics } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { breadcrumbSchema } from "@/lib/structuredData";

const TOPICS_BREADCRUMB_LD = breadcrumbSchema([
  { name: "Home", path: "/" },
  { name: "Topics", path: "/topics" },
]);
import { ArrowRight, Calculator, Divide, Percent, Triangle, FunctionSquare, Sigma, Waves, BarChart3 } from "lucide-react";

const TOPIC_ICONS = {
  addition: Calculator,
  subtraction: Calculator,
  multiplication: Calculator,
  division: Divide,
  fractions: Sigma,
  decimals: Sigma,
  percentages: Percent,
  algebra: FunctionSquare,
  geometry: Triangle,
  trigonometry: Waves,
  statistics: BarChart3,
};

const TOPIC_COLORS = {
  Arithmetic: "bg-brand-yellow",
  Numbers: "bg-brand-coral",
  Algebra: "bg-brand-blue text-white",
  Geometry: "bg-brand-green",
  Trigonometry: "bg-brand-orange text-white",
  Statistics: "bg-brand-coral",
};

const STATIC_TOPICS = [
  { id: "addition", label: "Addition", category: "Arithmetic", min_grade: "K", max_grade: "5" },
  { id: "subtraction", label: "Subtraction", category: "Arithmetic", min_grade: "K", max_grade: "5" },
  { id: "multiplication", label: "Multiplication", category: "Arithmetic", min_grade: "2", max_grade: "6" },
  { id: "division", label: "Division", category: "Arithmetic", min_grade: "3", max_grade: "6" },
  { id: "place_value", label: "Place Value & Counting", category: "Number Sense", min_grade: "K", max_grade: "4" },
  { id: "money_time", label: "Money & Time", category: "Measurement", min_grade: "1", max_grade: "4" },
  { id: "fractions", label: "Fractions", category: "Numbers", min_grade: "3", max_grade: "8" },
  { id: "decimals", label: "Decimals", category: "Numbers", min_grade: "4", max_grade: "8" },
  { id: "percentages", label: "Percentages", category: "Numbers", min_grade: "5", max_grade: "9" },
  { id: "ratios", label: "Ratios & Rates", category: "Numbers", min_grade: "6", max_grade: "8" },
  { id: "integers", label: "Integers & Negative Numbers", category: "Numbers", min_grade: "6", max_grade: "8" },
  { id: "exponents", label: "Exponents & Scientific Notation", category: "Numbers", min_grade: "8", max_grade: "11" },
  { id: "algebra", label: "Algebra", category: "Algebra", min_grade: "6", max_grade: "12" },
  { id: "functions", label: "Functions", category: "Algebra", min_grade: "8", max_grade: "12" },
  { id: "geometry", label: "Geometry", category: "Geometry", min_grade: "3", max_grade: "12" },
  { id: "trigonometry", label: "Trigonometry", category: "Trigonometry", min_grade: "9", max_grade: "12" },
  { id: "statistics", label: "Statistics & Probability", category: "Statistics", min_grade: "5", max_grade: "12" },
  { id: "precalculus", label: "Pre-Calculus", category: "Pre-Calculus", min_grade: "11", max_grade: "12" },
  { id: "calculus", label: "Calculus", category: "Calculus", min_grade: "12", max_grade: "C" },
];

export default function Topics() {
  const [topics, setTopics] = useState(STATIC_TOPICS);
  useEffect(() => {
    getTopics().then((d) => { if (d.topics?.length) setTopics(d.topics); }).catch(() => {});
  }, []);

  const grouped = topics.reduce((acc, t) => {
    (acc[t.category] = acc[t.category] || []).push(t);
    return acc;
  }, {});

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>All Math Topics K-12 — iLoveMathWorksheets</title>
        <meta name="description" content="Browse every K-12 math topic — addition, fractions, algebra, geometry, calculus and more. Tap any topic to generate a free worksheet instantly." />
        <link rel="canonical" href="https://www.ilovemathworksheets.com/topics" />
        <script type="application/ld+json">{JSON.stringify(TOPICS_BREADCRUMB_LD)}</script>
      </Helmet>
      <Header />
      <main className="max-w-7xl w-full mx-auto px-5 sm:px-8 py-10 flex-1">
        <div className="mb-10">
          <span className="brutal-badge bg-brand-coral">Catalog</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mt-4">Every topic, K-12.</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">Tap a topic to jump straight into the generator.</p>
        </div>

        {Object.entries(grouped).map(([cat, topics]) => (
          <section key={cat} className="mb-12">
            <h2 className="font-heading text-2xl font-bold mb-5">{cat}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {topics.map((t) => {
                const Icon = TOPIC_ICONS[t.id] || Calculator;
                return (
                  <Link
                    key={t.id}
                    to="/"
                    className="brutal-card p-6 hover:-translate-y-1 hover:shadow-brutal-lg transition-all duration-200 group"
                    data-testid={`catalog-topic-${t.id}`}
                  >
                    <div className={`w-14 h-14 ${TOPIC_COLORS[t.category] || "bg-brand-yellow"} border-[3px] border-[#0A0A0A] rounded-xl flex items-center justify-center mb-4`}>
                      <Icon strokeWidth={2.75} className="w-7 h-7" />
                    </div>
                    <h3 className="font-heading text-xl font-bold">{t.label}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Grades {t.min_grade}–{t.max_grade}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold group-hover:gap-2 transition-all">
                      Generate <ArrowRight strokeWidth={3} className="w-4 h-4" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        ))}
      </main>
      <Footer />
    </div>
  );
}
